import { add, money, toDb, ZERO } from '@miraki/money';
import type { PurchaseInvoiceDetail } from '@miraki/contracts';

/**
 * A purchase invoice's pieces, read as PRODUCTS: one row per product and variant at one price, with
 * how many and what they come to. The pieces underneath are unchanged (an invoice still covers
 * individual pieces, FN-04); this is only how they are listed, the same way A-40's Awaiting invoice
 * reads them. Money only through `@miraki/money`.
 */
export interface InvoiceProduct {
  key: string;
  imageUrl: string | null;
  /** The product's own name; the long description only where a name is missing. */
  name: string;
  /** The full variant code; colour and size where a piece has no code. */
  variant: string;
  quantity: number;
  /** Price of one, times how many. */
  amount: string;
}

type Piece = PurchaseInvoiceDetail['pieces'][number];

export function invoiceProducts(pieces: readonly Piece[]): InvoiceProduct[] {
  const by = new Map<string, InvoiceProduct>();
  for (const p of pieces) {
    const name = p.productName ?? p.description;
    const variant = p.variantCode ?? ([p.goldColour, p.ringSize].filter(Boolean).join(' · ') || '—');
    const key = [name, variant, p.wholesaleValue].join('|');
    const g = by.get(key);
    if (g) {
      g.quantity += 1;
      g.amount = toDb(add(money(g.amount), money(p.wholesaleValue)));
      if (!g.imageUrl && p.imageUrl) g.imageUrl = p.imageUrl;
      continue;
    }
    by.set(key, { key, imageUrl: p.imageUrl ?? null, name, variant, quantity: 1, amount: toDb(add(ZERO, money(p.wholesaleValue))) });
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name) || a.variant.localeCompare(b.variant));
}
